import React, { useEffect, useId, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface HelpTooltipProps {
  content: string;
  learnMoreHref?: string;
  learnMoreLabel?: string;
  side?: 'top' | 'bottom';
  className?: string;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  learnMoreHref,
  learnMoreLabel = 'Más información',
  side = 'top',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const positionClass = side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';

  return (
    <div
      ref={rootRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Más información"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        className="help-tooltip-trigger"
      >
        <HelpCircle className="w-4 h-4" strokeWidth={2} />
      </button>

      {open && (
        <div
          id={id}
          role="tooltip"
          className={`help-tooltip-panel ${positionClass}`}
        >
          <p>{content}</p>
          {learnMoreHref && (
            <Link to={learnMoreHref} className="help-tooltip-link" onClick={() => setOpen(false)}>
              {learnMoreLabel} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;
